import { useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { DemoChip } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { PHONE_TREES_MIN_DATE } from '../../constants';
import { useFetchAggregatedPhoneTreeCallVolume } from '../../hooks/use-fetch-aggregated-phone-tree-call-volume';
import { trackingIds } from '../../tracking-ids';
import { transformChartData } from '../../utils/transform-phone-tree-chart-data';
import { PhoneAnalyticsEntity } from '../call-group-queue-charts/types';
import { EmptyStateAnalyticsView } from '../empty-state-analytics';
import { useIsRoutingAnalyticsEmbedded, useRoutingAnalyticsData } from '../routing-analytics-context';
import { PhoneTreeCarouselView } from './phone-tree-call-volume-carousel-view';

export const PhoneTreesCallVolume = () => {
  const { t } = useTranslation('analytics');
  const { filters, isDemoAccount, filterHintText, previousGlsLocationIds, demoData } = useRoutingAnalyticsData();
  const isEmbedded = useIsRoutingAnalyticsEmbedded();

  const navigate = useNavigate();

  const { data, isLoading, allPhoneNumbers, locationNames, isDateValid } = useFetchAggregatedPhoneTreeCallVolume({
    filters,
    isDemoAccount,
    previousGlsLocationIds,
    demoData,
  });

  const carouselDataView = useMemo(() => {
    return transformChartData({ data, allPhoneNumbers, locationNames }).slice(0, 6);
  }, [locationNames, data, allPhoneNumbers]);

  // Track data version to force chart remounts when underlying data changes
  const dataVersionRef = useRef(0);
  const prevCarouselDataRef = useRef(carouselDataView);
  if (prevCarouselDataRef.current !== carouselDataView) {
    prevCarouselDataRef.current = carouselDataView;
    dataVersionRef.current += 1;
  }

  const hasPhoneTreeDialOptions = carouselDataView.some((item) => !!item.dialOptions && item.dialOptions?.length > 0);

  const showNoDataAvailable = data?.length === 0 || !isDateValid || !hasPhoneTreeDialOptions;

  return (
    <>
      <Heading level={2}>{t('Phone Trees')}</Heading>
      <Chart isLoading={isLoading} trackingId={trackingIds.routingAnalyticsCharts} style={{ marginBottom: 0 }}>
        <Chart.Header
          actions={[
            {
              label: t('View All ({{ numOfDataLength }})', { numOfDataLength: data?.length }),
              onClick: () => {
                navigate({
                  to: '/analytics/phone/phone-tree-call-volume-all',
                  search: { from: isEmbedded ? 'communication' : undefined },
                });
              },
              omit: (data?.length ?? 0) <= 6 || showNoDataAvailable,
            },
          ]}
          title={t('Call Volume by Phone Tree')}
          bottomElement={
            <Text color='subdued'>{t('View the number of calls received through each dial option.')}</Text>
          }
          subtitle={filterHintText}
          css={styles.header}
          leftElement={isDemoAccount && <DemoChip />}
        />
        {showNoDataAvailable ? (
          <EmptyStateAnalyticsView
            isDateValid={isDateValid}
            entityType={PhoneAnalyticsEntity.PhoneTrees}
            minAllowedDate={PHONE_TREES_MIN_DATE}
          />
        ) : (
          <PhoneTreeCarouselView data={carouselDataView} dataVersion={dataVersionRef.current} />
        )}
      </Chart>
    </>
  );
};

const styles = {
  header: css`
    .actions-wrapper {
      flex-direction: row-reverse;
      flex: 1 1 50%;

      button {
        font-weight: ${theme.font.weight.bold};
      }
    }
  `,
};
